import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { galleriesApi } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { ArrowLeft, Upload, X, ZoomIn, Camera } from 'lucide-react';
import { toast } from 'sonner';

const GalleryDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [gallery, setGallery] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadData, setUploadData] = useState({ title: '', photo_data: '' });

  useEffect(() => {
    loadGallery();
  }, [id]);

  const loadGallery = async () => {
    try {
      const data = await galleriesApi.getOne(id);
      setGallery(data);
      setPhotos(data.photos || []);
    } catch (error) {
      console.error('Error loading gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadData({ ...uploadData, photo_data: e.target.result });
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!uploadData.photo_data) {
      toast.error('Please select a photo');
      return;
    }

    setUploading(true);
    try {
      await galleriesApi.uploadPhoto(id, {
        title: uploadData.title,
        photo_data: uploadData.photo_data,
        uploaded_by_name: user?.name || 'Anonymous'
      });
      toast.success('Photo uploaded! It will appear after admin approval.');
      setUploadDialogOpen(false);
      setUploadData({ title: '', photo_data: '' });
      loadGallery();
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[50vh] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-700 mx-auto"></div>
            <p className="mt-4 text-stone-600">Loading gallery...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!gallery) {
    return (
      <Layout>
        <div className="min-h-[50vh] flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="p-8 text-center">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">Gallery Not Found</h2>
              <Link to="/galleries">
                <Button>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Galleries
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <section className="bg-stone-800 py-12">
        <div className="max-w-7xl mx-auto px-4">
          <Link to="/galleries" className="inline-flex items-center text-amber-400 hover:text-amber-300 mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Galleries
          </Link>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-serif font-bold text-white mb-2">
                {gallery.title}
              </h1>
              {gallery.description && (
                <p className="text-stone-300">{gallery.description}</p>
              )}
            </div>
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-amber-700 hover:bg-amber-800">
                  <Upload className="mr-2 h-4 w-4" />
                  Upload Photo
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Upload Photo</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Photo *</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="cursor-pointer"
                    />
                    {uploadData.photo_data && (
                      <div className="mt-2 relative">
                        <img
                          src={uploadData.photo_data}
                          alt="Preview"
                          className="w-full h-40 object-cover rounded-lg"
                        />
                        <Button
                          size="sm"
                          variant="destructive"
                          className="absolute top-2 right-2"
                          onClick={() => setUploadData({ ...uploadData, photo_data: '' })}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label>Title (optional)</Label>
                    <Input
                      value={uploadData.title}
                      onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
                      placeholder="Enter a title for your photo"
                    />
                  </div>
                  <p className="text-sm text-stone-500">
                    Photos will be visible after admin approval.
                  </p>
                  <Button
                    onClick={handleUpload}
                    disabled={uploading || !uploadData.photo_data}
                    className="w-full bg-amber-700 hover:bg-amber-800"
                  >
                    {uploading ? 'Uploading...' : 'Upload Photo'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </section>

      {/* Photos Grid */}
      <section className="py-12 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4">
          {photos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map((photo) => (
                <div
                  key={photo.id}
                  className="relative group cursor-pointer"
                  onClick={() => setSelectedPhoto(photo)}
                >
                  <div className="aspect-square bg-stone-200 rounded-lg overflow-hidden">
                    <img
                      src={photo.photo_data || photo.photo_url}
                      alt={photo.title || 'Gallery photo'}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors rounded-lg flex items-center justify-center">
                    <ZoomIn className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Card className="max-w-2xl mx-auto">
              <CardContent className="p-8 text-center">
                <Camera className="h-12 w-12 text-stone-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-stone-800 mb-2">No Photos Yet</h3>
                <p className="text-stone-600 mb-4">
                  Be the first to upload a photo to this gallery!
                </p>
                <Button onClick={() => setUploadDialogOpen(true)} className="bg-amber-700 hover:bg-amber-800">
                  <Upload className="mr-2 h-4 w-4" />
                  Upload Photo
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <Button
            variant="ghost"
            className="absolute top-4 right-4 text-white hover:bg-white/20"
            onClick={() => setSelectedPhoto(null)}
          >
            <X className="h-6 w-6" />
          </Button>
          <img
            src={selectedPhoto.photo_data || selectedPhoto.photo_url}
            alt={selectedPhoto.title || 'Gallery photo'}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {selectedPhoto.title && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 px-4 py-2 rounded-lg">
              <p className="text-white">{selectedPhoto.title}</p>
              {selectedPhoto.uploaded_by_name && (
                <p className="text-stone-400 text-sm">by {selectedPhoto.uploaded_by_name}</p>
              )}
            </div>
          )}
        </div>
      )}
    </Layout>
  );
};

export default GalleryDetailPage;
